import React, { FC } from 'react';
import Img from './Img';
import styled from 'styled-components';
import createImgProps, { ImageType } from '../utils/createImgProps';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Accomodation from './svg/Accomodation';
import Activity from './svg/Activity';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import Download from './svg/Download';
import downloadTorFile from '../utils/downloadTORFile';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import { Box } from '@rebass/grid';
import router from 'next/router';
import Link from 'next/link';
import Anchor from './designsystem/Anchor';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { Locale } from '../entities/Locale';
import { getTripDuration } from '../utils/trip';
import { useTenantContext } from '../context/TenantContext';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Stack from './Stack';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import AsyncButton from './AsyncButton';

interface Props {
    thumbnail?: (ImageType & { type?: string }) | null;
    webThumbnail?: ImageType | null;
    orderItemTitle: string;
    arrivalDate: string;
    departureDate: string;
    orderItemTypeName: string;
    orderId: string;
    orderNumber?: string | null;
    voucherUrl: string;
    alterable?: boolean;
    cancellable?: boolean;
    itemId?: string;
    orderItemType: 'Activity' | 'Accomodation';
    duration?: string;
    isUnavailable?: boolean;
    isCancelled?: boolean;
}

const OrderItemTile: FC<React.PropsWithChildren<Props>> = ({
    thumbnail,
    webThumbnail,
    orderItemTitle,
    arrivalDate,
    departureDate,
    orderItemTypeName,
    isUnavailable,
    orderId,
    orderNumber,
    voucherUrl,
    alterable: isAlterable,
    cancellable: isCancellable,
    itemId,
    orderItemType,
    duration,
    isCancelled,
}) => {
    const cancellableModal = useSesame();
    const device = useDeviceSize();
    const { brandConfig } = useTenantContext();

    return (
        <>
            <OrderContentWrapper isUnavailable={isUnavailable}>
                <ContentWrapper>
                    {thumbnail && webThumbnail && (
                        <ThumbnailWrapper>
                            <ThumbnailContainer>
                                <Img {...createImgProps(thumbnail, webThumbnail)} layout="fill" />
                            </ThumbnailContainer>
                        </ThumbnailWrapper>
                    )}
                    <InfoWrapper>
                        <EyeBrowWrapper>
                            {orderItemType === 'Accomodation' && <StyledAccomodation />}
                            {orderItemType === 'Activity' && <StyledActivity />}
                            <EyeBrow>
                                <FormattedMessage
                                    defaultMessage="{orderItemTypeName}"
                                    values={{
                                        orderItemTypeName,
                                    }}
                                />
                            </EyeBrow>
                        </EyeBrowWrapper>
                        <div>
                            <StyledUnitTitle>{orderItemTitle}</StyledUnitTitle>
                        </div>
                        <ContentFooterWrapper>
                            <StyledDate>
                                <FormattedDate format="DAYMONTHYEAR" value={new Date(arrivalDate)} />
                            </StyledDate>
                            <DescriptionDivider>•</DescriptionDivider>
                            {orderNumber && (
                                <OrderNumberWrapper>
                                    <StyledOrder>
                                        <FormattedMessage defaultMessage="Onderdeel van bevestiging:  " />
                                    </StyledOrder>
                                    <OrderNumber onClick={() => router.push(`/my/order/${orderId}`)}>
                                        {orderNumber}
                                    </OrderNumber>
                                </OrderNumberWrapper>
                            )}
                            <DurationDivider>-</DurationDivider>
                            <DurationWrapper>
                                <Duration>
                                    {orderItemType === 'Accomodation' && (
                                        <FormattedMessage
                                            defaultMessage=" {duration}"
                                            values={{
                                                date: (
                                                    <FormattedDate
                                                        format="DAYMONTHYEAR"
                                                        value={new Date(arrivalDate)}
                                                    />
                                                ),
                                                duration: (
                                                    <FormattedMessage
                                                        defaultMessage="{duration, number} {duration, plural, one {nacht} other {nachten}}"
                                                        values={{
                                                            duration: getTripDuration(arrivalDate, departureDate),
                                                        }}
                                                    />
                                                ),
                                            }}
                                        />
                                    )}
                                    {orderItemType === 'Activity' && (
                                        <FormattedMessage
                                            defaultMessage=" {duration}"
                                            values={{
                                                duration,
                                            }}
                                        />
                                    )}
                                </Duration>
                            </DurationWrapper>
                        </ContentFooterWrapper>
                    </InfoWrapper>
                </ContentWrapper>
                <ButtonWrapper>
                    <Stack
                        flex={device === 'mobile' || device === 'tablet' ? 1 : 'unset'}
                        variant={device === 'mobile' || device === 'tablet' ? 'default' : 'inline'}
                        spacing={4}
                    >
                        {isAlterable && (
                            <Link
                                href={{
                                    pathname: `/my/order/${orderId}/booking/${itemId}/alter`,
                                    query: { orderId },
                                }}
                                legacyBehavior
                            >
                                <Button as="div" variant="outline">
                                    <FormattedMessage defaultMessage="Wijzigen" />
                                </Button>
                            </Link>
                        )}

                        {isCancelled && (
                            <Box alignSelf="center">
                                <CancelledTag>Geannuleerd</CancelledTag>
                            </Box>
                        )}
                        {!isCancellable && !isCancelled && (
                            <>
                                {orderItemType !== 'Activity' && (
                                    <Button onClick={cancellableModal.onOpen} variant="outline">
                                        <FormattedMessage defaultMessage="Annuleren" />
                                    </Button>
                                )}
                                <Modal onClose={cancellableModal.onClose} open={cancellableModal.open} variant="small">
                                    <ModalHeader variant="small">
                                        <FormattedMessage defaultMessage="Boeking annuleren" />
                                    </ModalHeader>
                                    <Box my={5}>
                                        <FormattedMessage defaultMessage="Neem contact met ons op om deze boeking te annuleren." />
                                        <Anchor
                                            as="a"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            href={brandConfig.contact.page?.[router.locale as Locale]}
                                        >
                                            <FormattedMessage defaultMessage="Neem contact op" />
                                        </Anchor>
                                    </Box>
                                </Modal>
                            </>
                        )}
                        {isCancellable && !isCancelled && (
                            <Link href={`/my/order/${orderId}/booking/${itemId}/cancel`} legacyBehavior>
                                <Button as="div" variant="outline">
                                    <FormattedMessage defaultMessage="Annuleren" />
                                </Button>
                            </Link>
                        )}

                        <StyledButton onClick={() => downloadTorFile(voucherUrl, orderItemTitle)}>
                            <Download />
                            {orderItemType === 'Accomodation' && <FormattedMessage defaultMessage="Boekingsdetails" />}
                            {orderItemType === 'Activity' && <FormattedMessage defaultMessage="Tickets" />}
                        </StyledButton>
                    </Stack>
                </ButtonWrapper>
            </OrderContentWrapper>
            <StyledDivider />
        </>
    );
};

export default OrderItemTile;

const ThumbnailContainer = styled.div`
    position: relative;
    margin-right: ${({ theme }) => theme.spacing['50_Semi']};
    width: 16rem;
    height: 12rem;
    overflow: hidden;
    border-radius: 0.8rem;

    img {
        object-fit: cover;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        border-radius: 0.8rem;
        flex-shrink: 0;
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 8rem;
        height: 8rem;
    }

    @media screen and (max-width: 320px) {
        width: 6rem;
        height: 6rem;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    align-items: center;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        margin-bottom: 2rem;
    }
`;

const EyeBrowWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0.4rem;
`;

const EyeBrow = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const StyledAccomodation = styled(Accomodation)`
    margin-right: 0.4rem;
`;

const StyledActivity = styled(Activity)`
    margin-right: 0.4rem;
`;

const StyledUnitTitle = styled(Title)`
    ${({ theme }) => getFontCss(theme.fontSizes.title.small)};
    margin-bottom: 1.2rem;
`;

const StyledDate = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const StyledOrder = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
    margin-right: 0.4rem;
`;

const DescriptionDivider = styled.span`
    margin: 0 0.8rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;

const OrderNumberWrapper = styled.div`
    display: flex;
    align-items: center;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;

const ContentFooterWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const OrderNumber = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
    color: ${({ theme }) => theme.colors.primary[80]};
    font-weight: 600;
    text-decoration: underline;
    text-decoration-thickness: 0.2rem;
    text-underline-offset: 0.4rem;
    cursor: pointer;
`;

const ButtonWrapper = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        width: 100%;
        justify-content: flex-start;
    }
`;

const OrderContentWrapper = styled.div<{
    isUnavailable?: boolean;
}>`
    display: flex;
    margin-bottom: 3.2rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        display: block;
    }

    ${({ isUnavailable }) =>
        isUnavailable &&
        `
        opacity: 0.5;
    `}
`;

const StyledDivider = styled.hr`
    border: 1px solid rgba(0, 0, 0, 0.17);
    margin-bottom: 2.4rem;
`;

const ThumbnailWrapper = styled.div`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 25%;
    }
`;

const InfoWrapper = styled.div`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 75%;
        padding-left: 1.2rem;
    }
`;

const StyledButton = styled(AsyncButton)`
    min-width: 18rem;
`;

const DurationWrapper = styled.div`
    display: none;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: flex;
        align-items: center;
    }
`;

const Duration = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const DurationDivider = styled.span`
    display: none;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: block;
        margin: 0 0.8rem;
    }
`;

const CancelledTag = styled.div`
    display: flex;
    align-items: center;
    padding: 0 0.4rem 0 0.6rem;
    border: 1px solid rgba(62, 113, 169, 0.25);
    border-radius: 0.4rem;
    font-size: ${({ theme }) => getFontCss(theme.fontSizes.body.tiny)};
    background-color: ${({ theme }) => theme.colorAssignments.main};
    margin-right: ${({ theme }) => theme.spacing['20_Tiny']};
`;
